import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DataTableContainer from '../../../common/components/DataTable/DataTableContainer'
import { Box, Button, debounce, TextField, Typography } from '@mui/material'
import LoadingIndicator from '../../../common/components/LoadingIndicator'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactComponent as SearchIcon } from '../../../common/icons/search.svg'
import useTrainingsUsersTable from './useTrainingsUsersTable'
import Spacer from 'src/common/components/Spacer'
import { GetReportButton, REPORT_PAGES } from 'src/common/components/GetReportButton'
import ConfirmationDialog from 'src/common/components/ConfirmationDialog'
import {
  useSendNotificationToAllUsersMutation,
  useSendNotificationToUserMutation
} from 'src/api/trainingPackages/trainingPackages'
import customToastWithAlert from 'src/common/components/CustomToastWithAlert'

export default function TrainingsUsers({ detailsHeight, training }) {
  const tableHeight = `calc(100vh - ${detailsHeight + 368}px)`
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState({ searchText: '' })
  const [showConfirmSendNotification, setShowConfirmSendNotification] = useState(null)

  const [sendNotoficationToAll, { isSuccess: isSuccessAll, isError: isErrorAll }] =
    useSendNotificationToAllUsersMutation()
  const [sendNotification, { isSuccess, isError }] = useSendNotificationToUserMutation()

  useEffect(() => {
    if (isSuccess || isError) {
      setShowConfirmSendNotification(null)
      customToastWithAlert({
        message: isError ? 'Failed to send notification' : 'Notification sent successfully',
        type: isError ? 'error' : 'success'
      })
    }
  }, [isError, isSuccess])

  useEffect(() => {
    if (isSuccessAll || isErrorAll) {
      setShowConfirmSendNotification(null)
      customToastWithAlert({
        message: isErrorAll ? 'Failed to send notification' : 'Notification sent successfully',
        type: isErrorAll ? 'error' : 'success'
      })
    }
  }, [isSuccessAll, isErrorAll])

  const { id } = useParams()
  const navigate = useNavigate()

  const onSelectUser = userId => {
    navigate(window.location.pathname + '/users-training-history/' + userId)
  }
  const onSendUserNotification = user => {
    setShowConfirmSendNotification(user)
  }

  const { columns, fetchData, totalCount, users, isLoading } = useTrainingsUsersTable({
    id,
    trainingStatus: training?.status,
    onSelectUser,
    onSendNotification: onSendUserNotification
  })

  const handleSendNotification = () => {
    if (typeof showConfirmSendNotification === 'object') {
      sendNotification({ id: id, userId: showConfirmSendNotification.userId })
    } else {
      sendNotoficationToAll(id)
    }
  }

  const debouncedSetParams = useMemo(
    () =>
      debounce(searchText => {
        setFilters(prevParams => ({ ...prevParams, searchText }))
      }, 500),
    [setFilters]
  )

  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value
      setSearchText(value)
      debouncedSetParams(value)
    },
    [debouncedSetParams]
  )

  if (isLoading) {
    return (
      <Box display={'flex'} height={'100vh'} alignItems={'center'}>
        <LoadingIndicator />
      </Box>
    )
  }

  console.log(training.status)

  return (
    <Box width={'100%'} pt={'64px'} display={'flex'} flexDirection={'column'} rowGap={'20px'}>
      <Box width={'100%'} display={'flex'} justifyContent={'space-between'}>
        <Typography noWrap fontWeight={900} color={'#1B1B26'} fontFamily={'Satoshi'} fontSize={'24px'} pb={'8px'}>
          Users in packages
        </Typography>
        <Box width='528px'>
          <TextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                border: '1px solid #E7E7EE',
                background: '#fff',
                width: '100%'
              }
            }}
            InputProps={{
              endAdornment: <SearchIcon />
            }}
            onChange={handleSearchChange}
            value={searchText}
            size='small'
            placeholder='Search by users'
          />
        </Box>
      </Box>
      <div className='row' style={{ margin: '-10px -14px' }}>
        <Spacer />

        {training.status == 'IN_PROGRESS' && (
          <Button
            variant={'outlined'}
            onClick={() => setShowConfirmSendNotification(true)}
            sx={{
              fontFamily: 'Plus Jakarta Sans',
              borderColor: '#E7E7EE',
              borderRadius: '12px',
              color: '#6123FF',
              textTransform: 'none'
            }}
          >
            Send notifications
          </Button>
        )}
        <GetReportButton page={REPORT_PAGES.TRAINING_BY_ID} id={id} />
      </div>
      <DataTableContainer
        onRequestData={fetchData}
        filter={filters}
        emptyText={'No users found'}
        config={{
          columns,
          stickyPagination: true,
          stickyHeader: true,
          height: tableHeight
        }}
        data={users}
        initialSort={{ orderBy: 'firstName', order: 'asc' }}
        totalCount={totalCount}
      />
      <ConfirmationDialog
        open={showConfirmSendNotification}
        title={typeof showConfirmSendNotification === 'object' ? 'Send notification' : 'Send notifications'}
        description={
          typeof showConfirmSendNotification === 'object'
            ? `Notification will be sent to ${showConfirmSendNotification?.firstName} ${showConfirmSendNotification?.lastName}`
            : 'Notifications will be sent to all users'
        }
        onCancel={() => setShowConfirmSendNotification(null)}
        onConfirm={handleSendNotification}
      />
    </Box>
  )
}
